import { createModel } from '@rematch/core';
import type { RootModel } from '@/components/WellLoggingOperations/types';
import type { GeoData, GeoState, Horizon, CommonPerfType, Permission } from './types';
import type { TestingData } from '@/api/api.v2';
import { api } from '@/api/api.v2';
import { assignUniqueStratumAndSeamKeys, getMinMaxDepth } from '@/components/GeoTest/utils';
import type { MessageInstance } from 'antd/es/message/interface';
import { HORIZONS } from '@/components/GeoTest/InitModal/constants';
import type { GeoUserProfile, GeoUserProfileData } from '@/components/GeoTest/types';
import { getWellInfo } from '@/components/GeoTest/UserProfilesModal/ProfileInfo';

const initialState: GeoState = {
    versionId: null,
    isRigis: false,
    selectedHorizon: HORIZONS,
    wellIds: [],

    dataError: false,
    geoData: null,
    perforationData: null,
    selectedPerforations: {},
    testingData: {},
    selectedTestings: {},
    min: null,
    max: null,

    userProfiles: [],
    activeUserProfile: null,

    isLoading: false,
    isUserProfilesLoading: false,
};

export const geoTest = createModel<RootModel>()({
    state: initialState,
    reducers: {
        setVersionId(state, payload: number | null) {
            return { ...state, versionId: payload };
        },
        setVersionIdIfNull(state, payload: number) {
            const updatedVersionId = state.versionId === null ? payload : state.versionId;
            return { ...state, versionId: updatedVersionId };
        },
        setSelectedHorizon(state, payload: Horizon[]) {
            return { ...state, selectedHorizon: payload };
        },
        resetHorizon(state) {
            const { selectedHorizon } = state;
            return { ...state, selectedHorizon: selectedHorizon.map(h => (h = { ...h, depth: null })) };
        },
        setWellIds(state, payload: number[]) {
            return { ...state, wellIds: payload };
        },
        setIsLoading(state, payload: boolean) {
            return { ...state, isLoading: payload };
        },
        setDataError(state, payload: boolean) {
            return { ...state, dataError: payload };
        },
        setGeoData(state, payload: GeoData | null) {
            return { ...state, geoData: payload };
        },
        setPerforationData(state, { wellId, data }: { wellId: number; data: CommonPerfType[] }) {
            return { ...state, perforationData: { ...state.perforationData, [wellId]: data } };
        },
        toggleSelectedPerforation(state, { wellId, id }: { wellId: number; id: number }) {
            const selected = state.selectedPerforations[wellId] ?? [];
            return {
                ...state,
                selectedPerforations: {
                    ...state.selectedPerforations,
                    [wellId]: selected.includes(id) ? selected.filter(tId => tId !== id) : [...selected, id],
                },
            };
        },
        toggleAllPerforation(state, { wellId }: { wellId: number }) {
            const anySelected = state.selectedPerforations[wellId].length > 0;
            const allPerforation = state.perforationData ? state.perforationData[wellId].map(({ id }) => id) : [];
            return {
                ...state,
                selectedPerforations: {
                    ...state.selectedPerforations,
                    [wellId]: anySelected ? [] : allPerforation,
                },
            };
        },
        setSelectedPerforations(state, { wellId, data }: { wellId: number; data: number[] }) {
            return { ...state, selectedPerforations: { ...state.selectedPerforations, [wellId]: data } };
        },
        setTestingData(state, payload: Record<number, TestingData[]>) {
            return { ...state, testingData: payload };
        },
        toggleSelectedTesting(state, { wellId, id }: { wellId: number; id: number }) {
            const selected = state.selectedTestings[wellId] ?? [];
            return {
                ...state,
                selectedTestings: {
                    ...state.selectedTestings,
                    [wellId]: selected.includes(id) ? selected.filter(tId => tId !== id) : [...selected, id],
                },
            };
        },
        setSelectedTestings(state, payload: Record<number, number[]>) {
            return { ...state, selectedTestings: payload };
        },
        setMin(state, payload: number | null) {
            return { ...state, min: payload };
        },
        setMax(state, payload: number | null) {
            return { ...state, max: payload };
        },
        setUserProfilesLoading(state, payload: boolean) {
            return { ...state, isUserProfilesLoading: payload };
        },
        setUserProfiles(state, payload: GeoUserProfile[]) {
            return { ...state, userProfiles: payload };
        },
        addUserProfile(state, payload: GeoUserProfile) {
            return { ...state, userProfiles: [payload, ...state.userProfiles] };
        },
        deleteUserProfile(state, payload: number) {
            return { ...state, userProfiles: state.userProfiles.filter(({ id }) => payload !== id) };
        },
        changeUserProfile(state, payload: GeoUserProfile) {
            return { ...state, userProfiles: [payload, ...state.userProfiles.filter(({ id }) => payload.id !== id)] };
        },
        setActiveUserProfile(state, payload: number | null) {
            return { ...state, activeUserProfile: payload };
        },
        setIsRigis(state, payload: boolean) {
            return { ...state, isRigis: payload };
        },
        applyProfile(state, profile: GeoUserProfile) {
            const { version: versionId, isRigis, gasHorizon, waterHorizon, wells } = profile;
            const wellIds = wells.map(({ wellId }) => wellId);
            const selectedPerforations: Record<number, number[]> = {};
            const selectedTestings: Record<number, number[]> = {};
            const selectedHorizon = HORIZONS.map(horizon => ({
                ...horizon,
                depth: horizon.name === 'ГНК' ? gasHorizon : waterHorizon,
            }));
            for (const well of wells) {
                const attr = getWellInfo(well);
                const { wellId } = well;
                Object.entries(attr).forEach(([key, data]) => {
                    if (key === 'perforations') {
                        selectedPerforations[wellId] = data;
                    }
                    if (key === 'testing') {
                        selectedTestings[wellId] = data;
                    }
                });
            }
            return {
                ...state,
                selectedHorizon,
                selectedPerforations,
                selectedTestings,
                wellIds,
                versionId,
                isRigis,
                isLoading: true,
            };
        },
        reset: () => initialState,
    },
    effects: {
        setVersionIdIfNull(payload: number, rootState) {
            const { versionId } = rootState.geoTest as GeoState;
            const updatedVersionId = versionId === null ? payload : versionId;
            this.setVersionId(updatedVersionId);
        },
        async loadGeoData({ messageApi, profileId }: { messageApi: MessageInstance; profileId?: number }, rootState) {
            const { wellIds, versionId: stratumVersionId, selectedHorizon, isRigis } = rootState.geoTest as GeoState;
            if (stratumVersionId !== null) {
                this.setIsLoading(true);
                const geoProfileRequest = api.geologicProfiles
                    .getGeologicProfiles({
                        wellsId: wellIds,
                        stratumVersionId,
                        waterHorizon: selectedHorizon[1].depth,
                        gasHorizon: selectedHorizon[0].depth,
                        // isRigis, // Даниил удалил этот флаг на бэке
                    })
                    .then(geoData => {
                        if (geoData) {
                            const convertedData = assignUniqueStratumAndSeamKeys(geoData, selectedHorizon);
                            convertedData?.wells?.forEach(w => {
                                if (w.isBadVersion) {
                                    void messageApi.open({
                                        type: 'warning',
                                        content: `Отсутствуют данные по пластам для скважины ${w.name}`,
                                    });
                                }
                            });
                            this.setGeoData(convertedData);
                            const [min, max] = getMinMaxDepth(convertedData);
                            this.setMin(min);
                            this.setMax(max);
                            this.setActiveUserProfile(profileId ? profileId : null);
                        }
                    })
                    .catch((error: Error) => {
                        console.error(error);
                        this.setIsLoading(false);
                        this.setDataError(true);
                    })
                    .finally(() => {
                        this.resetHorizon();
                    });

                const explorationRequest = api.carottage
                    .fetchTesting({
                        wellIds,
                    })
                    .then(({ testingByWell }) => {
                        const result: Record<number, TestingData[]> = {};
                        const selected: Record<number, number[]> = {};

                        for (const outerKey in testingByWell) {
                            const outerKeyNum = Number(outerKey);
                            result[outerKeyNum] = Object.values(testingByWell[outerKeyNum]);
                            selected[outerKeyNum] = Object.values(testingByWell[outerKeyNum]).map(({ id }) => id);
                        }

                        this.setTestingData(result);
                        if (!profileId) {
                            this.setSelectedTestings(selected);
                        }
                    });

                const getPerforationRequest = (wellId: number) =>
                    api.carottage
                        .fetchGraphData(wellId, JSON.stringify(['perforation', 'isolation']))
                        .then(({ perforation, isolation }) => {
                            const isolationArr = isolation ? Object.values(isolation).flat() : [];
                            const perforationArr = perforation ? Object.values(perforation).flat() : [];
                            this.setPerforationData({
                                wellId,
                                data: [
                                    ...perforationArr.map(({ perfId, ...rest }) => ({ id: perfId, ...rest })),
                                    ...isolationArr.map(({ isolationId, ...rest }) => ({
                                        id: Number(`${isolationId}${rest.roof}${rest.bottom}`),
                                        ...rest,
                                    })),
                                ],
                            });
                            if (!profileId) {
                                this.setSelectedPerforations({
                                    wellId,
                                    data: perforationArr.map(({ perfId }) => perfId),
                                });
                            }
                        });

                const requests: Array<Promise<void>> = [
                    geoProfileRequest,
                    explorationRequest,
                    ...wellIds.map(id => getPerforationRequest(id)),
                ];

                Promise.allSettled(requests)
                    .catch((error: Error) => {
                        console.error(error);
                        this.setIsLoading(false);
                        this.setDataError(true);
                    })
                    .finally(() => {
                        this.setIsLoading(false);
                    });
            } else {
                this.setIsLoading(false);
                this.setDataError(true);
            }
        },
        async loadUserProfiles() {
            this.setUserProfilesLoading(true);
            const userProfiles = await api.geologicProfiles.userProfiles.getAll();
            this.setUserProfiles(userProfiles);
            this.setUserProfilesLoading(false);
        },
        async addProfile(
            {
                profileName,
                permission,
                messageApi,
            }: { profileName: string; permission: Permission; messageApi: MessageInstance },
            rootState,
        ) {
            const { versionId, isRigis, selectedHorizon, geoData, selectedPerforations, selectedTestings } =
                rootState.geoTest as GeoState;
            const geoUserProfile: GeoUserProfileData = {
                profileName,
                gasHorizon: selectedHorizon.find(({ name }) => name === 'ГНК')?.depth ?? null,
                waterHorizon: selectedHorizon.find(({ name }) => name === 'ВНК')?.depth ?? null,
                version: versionId!,
                isRigis,
                isPrivate: permission === 'private',
                readOnly: permission === 'readonly',
                wells: geoData!.wells.map(({ wellId }) => ({
                    wellId,
                    attributes: JSON.stringify({
                        perforations: selectedPerforations[wellId],
                        testings: selectedTestings[wellId],
                    }),
                })),
            };

            const newProfile = await api.geologicProfiles.userProfiles.add(geoUserProfile);
            this.addUserProfile(newProfile[0]);
            void messageApi.success('Профиль успешно сохранен');
        },
        async deleteProfile({ id, messageApi }: { id: number; messageApi: MessageInstance }, rootState) {
            const { deleted } = await api.geologicProfiles.userProfiles.delete(id);
            const { activeUserProfile } = rootState.geoTest as GeoState;

            this.deleteUserProfile(Number(deleted));
            if (Number(deleted) === activeUserProfile) {
                this.reset();
            }
            void messageApi.success('Профиль успешно удален');
        },
        async changeProfile({ id: pId, messageApi }: { id: number; messageApi: MessageInstance }, rootState) {
            const {
                userProfiles,
                selectedHorizon,
                versionId,
                isRigis,
                geoData,
                selectedPerforations,
                selectedTestings,
            } = rootState.geoTest as GeoState;
            if (!geoData || !versionId) {
                messageApi.error('Для сохранения нужен построенный профиль');
                return;
            }
            const profile = userProfiles.find(({ id }) => id === pId)!;
            const { isPrivate, readOnly, profileName } = profile;
            const geoUserProfile: GeoUserProfileData = {
                profileName,
                gasHorizon: selectedHorizon.find(({ name }) => name === 'ГНК')?.depth ?? null,
                waterHorizon: selectedHorizon.find(({ name }) => name === 'ВНК')?.depth ?? null,
                version: versionId,
                isRigis,
                isPrivate,
                readOnly,
                wells: geoData.wells.map(({ wellId }) => ({
                    wellId,
                    attributes: JSON.stringify({
                        perforations: selectedPerforations[wellId],
                        testings: selectedTestings[wellId],
                    }),
                })),
            };

            const newProfile = await api.geologicProfiles.userProfiles.change(pId, geoUserProfile);

            this.changeUserProfile(newProfile[0]);
            void messageApi.success('Набор настроек успешно сохранен');
        },
    },
});
